import { useCallback, useEffect, useMemo } from 'react';
import { useAppSelector } from '../store/hooks';
import en from './locales/en';
import ar from './locales/ar';
import type { TranslationKey } from './keys';
import { isPluralValue, plural } from './plural';
import {
  RTL_LANGUAGES,
  resolveLanguage,
  setStoredLanguage,
  type SupportedLanguage,
} from './storage';

const LOCALES: Record<SupportedLanguage, Record<TranslationKey, string>> = {
  en: en as Record<TranslationKey, string>,
  ar,
};

export type TranslateParams = Record<string, string | number>;

export function translate(
  language: SupportedLanguage,
  key: TranslationKey,
  params?: TranslateParams
): string {
  const catalog = LOCALES[language] ?? en;
  let text = catalog[key] ?? en[key] ?? key;

  const count = params?.count;
  if (typeof count === 'number' && isPluralValue(text)) {
    text = plural(text, count);
  }

  if (params) {
    for (const [paramKey, value] of Object.entries(params)) {
      text = text.split(`{{${paramKey}}}`).join(String(value));
    }
  }

  return text;
}

export function applyDocumentLanguage(language: SupportedLanguage): void {
  document.documentElement.lang = language;
  document.documentElement.dir = RTL_LANGUAGES.has(language) ? 'rtl' : 'ltr';
}

export function useTranslation() {
  const gameSettingsLanguage = useAppSelector(
    state => state.game.gameSettings?.language
  );
  const previewLanguage = useAppSelector(state => state.ui.languagePreview);

  const language = useMemo(
    () => resolveLanguage(gameSettingsLanguage, previewLanguage),
    [gameSettingsLanguage, previewLanguage]
  );

  const t = useCallback(
    (key: TranslationKey, params?: TranslateParams) =>
      translate(language, key, params),
    [language]
  );

  const isRtl = RTL_LANGUAGES.has(language);

  return { t, language, isRtl };
}

/** Syncs `lang` / `dir` on `<html>` when the active locale changes. */
export function DocumentLanguageSync() {
  const { language } = useTranslation();

  useEffect(() => {
    applyDocumentLanguage(language);
    setStoredLanguage(language);
  }, [language]);

  return null;
}

export { plural } from './plural';
export {
  resolveLanguage,
  setStoredLanguage,
  getStoredLanguage,
} from './storage';
export type { TranslationKey } from './keys';
